import is from '@sindresorhus/is';
import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { JobRole } from '~/shared/data-contract/job-role';
import type { JobRoleDetails } from '~/shared/data-contract/job-role-details';

import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import { jobArchitectureKeys } from './query-keys';

export type UseDeleteJobRolesParams = {
    readonly jobRoles: NonEmptyArray<JobRole | JobRoleDetails>;
};

export type UseDeleteJobRolesResult = UseMutationResult<unknown, Error, UseDeleteJobRolesParams>;

export const useDeleteJobRoles = (): UseDeleteJobRolesResult => {
    const { deleteJobRoles } = useApplicationFetchers();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ jobRoles }: UseDeleteJobRolesParams) => {
            const result = await deleteJobRoles(jobRoles);

            if (result.isErr) {
                throw result.error;
            }

            return result.value;
        },
        onMutate: (params) => {
            const { architectureId } = params.jobRoles[0];
            const clusterIds = new Set<string>();

            params.jobRoles.forEach((role) => {
                if (!is.nullOrUndefined(role.parentId)) {
                    clusterIds.add(role.parentId);
                }
            });

            clusterIds.forEach((clusterId) => {
                queryClient.cancelQueries({
                    queryKey: jobArchitectureKeys.rolesByCluster(architectureId, clusterId),
                });

                queryClient.setQueryData(
                    jobArchitectureKeys.rolesByCluster(architectureId, clusterId),
                    (old: readonly JobRole[] | undefined) => {
                        return old?.filter((jobRole) => !params.jobRoles.includes(jobRole));
                    },
                );
            });

            queryClient.cancelQueries({
                queryKey: jobArchitectureKeys.archivedRoles(architectureId),
            });

            const deletedIds = new Set(params.jobRoles.map((role) => role.id));
            queryClient.setQueryData(
                jobArchitectureKeys.archivedRoles(architectureId),
                (old: readonly JobRole[] | undefined) => old?.filter((role) => !deletedIds.has(role.id)),
            );
        },
        onSuccess: (_, { jobRoles }) => {
            const { architectureId } = jobRoles[0];

            queryClient.invalidateQueries({
                queryKey: jobArchitectureKeys.architecture(architectureId),
            });
            queryClient.invalidateQueries({
                queryKey: jobArchitectureKeys.architectureDetail(architectureId),
            });
        },
        throwOnError: false,
    });
};
